import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { createChangeRequest, getPlaces, listAssignees, listChangeRequests, listDepartmentApprovers, listDepartments, listItSystems, updateChangeRequest, uploadChangeAttachment } from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { useFavorites } from "../lib/useFavorites";
import { formatBytes, formatChangeNumber, formatDateShort } from "../lib/format";
import { useChangeNumberFormat } from "../lib/changeNumbering";
import type { ChangeOption, ChangeRequestRow, CreateChangeRequestInput } from "../lib/types";
import {
  ChangeConfigProvider,
  useChangeOptions,
  type ChangeConfigIndex,
} from "../lib/changeConfig";
import { PageBar } from "../components/Layout";
import { Avatar } from "../components/Avatar";
import { getCurrentUser } from "../lib/auth";
import { Modal } from "../components/Modal";
import { RichTextEditor, richTextIsEmpty } from "../components/RichText";
import { Alert, EmptyState, ErrorState, Loading } from "../components/States";

// Stable work-type id for this module: "change_management".

type ChangeView = "list" | "board";

/** Status pill for a change request — resolves label + color from the tenant's
 *  configured statuses (falls back to the code/default accent). */
export function ChangeStatusBadge({ status }: { status: string }) {
  const cfg = useChangeOptions();
  const opt = cfg.statuses.find((o) => o.code === status);
  return (
    <span
      title="Status"
      className={`badge change-st change-st-${status}`}
      style={opt?.color ? { background: opt.color, color: "#fff", borderColor: opt.color } : undefined}
    >
      {opt?.label ?? status}
    </span>
  );
}

/** Risk pill for a change request (label + color from config). */
export function ChangeRiskBadge({ risk }: { risk: string }) {
  const cfg = useChangeOptions();
  const opt = cfg.risks.find((o) => o.code === risk);
  return (
    <span
      title="Risk"
      className={`badge change-risk change-risk-${risk}`}
      style={opt?.color ? { background: opt.color, color: "#fff", borderColor: opt.color } : undefined}
    >
      {opt?.label ?? risk}
    </span>
  );
}

/** Generic colored pill for a change-request value set (type / urgency / impact)
 *  — resolves label + color from the given option list. Renders nothing when the
 *  value is empty so callers can drop it in unconditionally. */
function ChangeOptionBadge({
  value,
  options,
  kind,
}: {
  value: string | null | undefined;
  options: ChangeOption[];
  kind: string;
}) {
  if (!value) return null;
  const opt = options.find((o) => o.code === value);
  return (
    <span
      title={kind.charAt(0).toUpperCase() + kind.slice(1)}
      className={`badge change-${kind} change-${kind}-${value}`}
      style={opt?.color ? { background: opt.color, color: "#fff", borderColor: opt.color } : undefined}
    >
      {opt?.label ?? value}
    </span>
  );
}

/** Type pill for a change request (label + color from config). */
export function ChangeTypeBadge({ type }: { type: string | null | undefined }) {
  const cfg = useChangeOptions();
  return <ChangeOptionBadge value={type} options={cfg.types} kind="type" />;
}

/** Urgency / priority pill for a change request (label + color from config). */
export function ChangeUrgencyBadge({ urgency }: { urgency: string | null | undefined }) {
  const cfg = useChangeOptions();
  return <ChangeOptionBadge value={urgency} options={cfg.urgencies} kind="urgency" />;
}

/** Business-impact pill for a change request (label + color from config). */
export function ChangeImpactBadge({ impact }: { impact: string | null | undefined }) {
  const cfg = useChangeOptions();
  return <ChangeOptionBadge value={impact} options={cfg.impacts} kind="impact" />;
}

/** Convert a unix-seconds timestamp to a yyyy-mm-dd value for a date input. */
export function unixToDateInput(ts: number | null | undefined): string {
  if (!ts) return "";
  const d = new Date(ts * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Convert a yyyy-mm-dd date-input value to unix seconds (local midnight), or null. */
export function dateInputToUnix(v: string): number | null {
  if (!v) return null;
  const ms = Date.parse(`${v}T00:00:00`);
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
}

/** Change Management queue — wraps everything in the config provider so the
 *  badges/board resolve labels + colors from the tenant's value sets. */
export function ChangeManagementPage() {
  return (
    <ChangeConfigProvider>
      <ChangeManagementInner />
    </ChangeConfigProvider>
  );
}

function ChangeManagementInner() {
  const navigate = useNavigate();
  const cfg = useChangeOptions();
  const numFmt = useChangeNumberFormat();

  const myId = getCurrentUser()?.id ?? "";
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  // Quick filters (mirror the ticket inbox): a status quick-pick + "Mine" +
  // risk / department dropdowns. All applied server-side.
  const [quick, setQuick] = useState<"all" | "mine" | "created" | "favorites">("all");
  const { isFavorite } = useFavorites();
  const [statusFilter, setStatusFilter] = useState("");
  const [riskFilter, setRiskFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const departmentsApi = useApi((s) => listDepartments(s), []);
  const cmDepartments = (departmentsApi.data?.departments ?? []).filter((d) => d.change_management);
  // View preference (List ↔ Kanban board), remembered across visits.
  const [view, setView] = useState<ChangeView>(() => {
    const v = localStorage.getItem("ac.change.view");
    return v === "board" ? "board" : "list";
  });
  useEffect(() => {
    localStorage.setItem("ac.change.view", view);
  }, [view]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data, loading, error, reload } = useApi(
    (signal) =>
      listChangeRequests(
        {
          ...(debounced ? { q: debounced } : {}),
          ...(quick === "mine" && myId ? { assignee: myId } : {}),
          ...(view === "list" && statusFilter ? { status: statusFilter } : {}),
          ...(riskFilter ? { risk: riskFilter } : {}),
          ...(departmentFilter ? { department_id: departmentFilter } : {}),
        },
        signal,
      ),
    [debounced, quick, statusFilter, riskFilter, departmentFilter, view, myId],
  );
  const allRequests = data?.requests ?? [];
  // "Created by me" and "Favorites" filter the loaded set client-side (no server
  // param needed).
  const requests =
    quick === "created" && myId
      ? allRequests.filter((r) => r.created_by === myId)
      : quick === "favorites"
        ? allRequests.filter((r) => isFavorite("change", r.id))
        : allRequests;

  return (
    <>
      <PageBar
        crumbs={<span className="crumb-current">Change Management</span>}
        actions={
          <>
            <div className="seg" role="group" aria-label="Change view">
              <button
                type="button"
                className={`seg-btn${view === "list" ? " active" : ""}`}
                aria-pressed={view === "list"}
                onClick={() => setView("list")}
              >
                List
              </button>
              <button
                type="button"
                className={`seg-btn${view === "board" ? " active" : ""}`}
                aria-pressed={view === "board"}
                onClick={() => setView("board")}
              >
                Board
              </button>
            </div>
            <button className="btn primary" type="button" onClick={() => setShowCreate(true)}>
              + New Change
            </button>
          </>
        }
      />

      {view === "board" ? (
        <ChangeBoard
          statuses={cfg.statuses}
          requests={requests}
          loading={loading}
          error={error}
          reload={reload}
          onOpen={(id) => navigate(`/change-management/${id}`)}
        />
      ) : (
        <div className="inbox change-inbox">
          <section className="inbox-list" aria-label="Change requests">
            <div className="inbox-list-head">
              <div className="inbox-search">
                <span className="search-icon" aria-hidden="true">🔍</span>
                <input
                  type="text"
                  className="search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search title or number…"
                  aria-label="Search change requests"
                />
              </div>
              <div className="inbox-filters" role="group" aria-label="Quick filters">
                <button
                  type="button"
                  className={`inbox-filter${quick === "all" ? " active" : ""}`}
                  onClick={() => setQuick("all")}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`inbox-filter${quick === "mine" ? " active" : ""}`}
                  onClick={() => setQuick("mine")}
                  disabled={!myId}
                  title={myId ? "Assigned to me" : "Sign-in required"}
                >
                  Mine
                </button>
                <button
                  type="button"
                  className={`inbox-filter${quick === "created" ? " active" : ""}`}
                  onClick={() => setQuick("created")}
                  disabled={!myId}
                  title={myId ? "Changes I created" : "Sign-in required"}
                >
                  Created by me
                </button>
                <button
                  type="button"
                  className={`inbox-filter${quick === "favorites" ? " active" : ""}`}
                  onClick={() => setQuick("favorites")}
                  title="Changes you've starred"
                >
                  ★ Favorites
                </button>
                <select
                  className="inbox-priority"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  aria-label="Filter by status"
                >
                  <option value="">Any status</option>
                  {cfg.statuses.map((o) => (
                    <option key={o.code} value={o.code}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <select
                  className="inbox-priority"
                  value={riskFilter}
                  onChange={(e) => setRiskFilter(e.target.value)}
                  aria-label="Filter by risk"
                >
                  <option value="">Any risk</option>
                  {cfg.risks.map((o) => (
                    <option key={o.code} value={o.code}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <select
                  className="inbox-priority"
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  aria-label="Filter by department"
                >
                  <option value="">Any department</option>
                  {cmDepartments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="inbox-list-body">
              {loading ? (
                <Loading />
              ) : error ? (
                <ErrorState error={error} onRetry={reload} />
              ) : requests.length === 0 ? (
                <div style={{ padding: 18 }}>
                  <EmptyState>No change requests match.</EmptyState>
                </div>
              ) : (
                <ul className="inbox-rows">
                  {requests.map((r) => (
                    <li
                      key={r.id}
                      className="inbox-row"
                      role="button"
                      tabIndex={0}
                      aria-label={`Change request: ${r.title}`}
                      onClick={() => navigate(`/change-management/${r.id}`)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(`/change-management/${r.id}`);
                        }
                      }}
                    >
                      <Avatar name={r.assignee_name ?? r.title} size="md" />
                      <div className="inbox-row-main">
                        <div className="inbox-row-top">
                          <span className="inbox-row-subject">{r.title}</span>
                          <span className="inbox-row-num mono faint">#{formatChangeNumber(r.number, numFmt)}</span>
                        </div>
                        <div className="inbox-row-snip">
                          {r.department_name ? <span className="inbox-row-req">{r.department_name}</span> : null}
                          {r.scheduled_start ? (
                            <span className="faint">
                              {r.department_name ? " · " : ""}
                              {formatDateShort(r.scheduled_start)}
                              {r.scheduled_end ? ` – ${formatDateShort(r.scheduled_end)}` : ""}
                            </span>
                          ) : null}
                        </div>
                        <div className="inbox-row-meta">
                          <ChangeStatusBadge status={r.status} />
                          <ChangeRiskBadge risk={r.risk} />
                          <ChangeTypeBadge type={r.change_type} />
                          <ChangeUrgencyBadge urgency={r.urgency} />
                          <ChangeImpactBadge impact={r.business_impact} />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
          <section className="inbox-read" aria-label="Change request detail">
            <div className="inbox-read-empty">
              <h2>Select a change request</h2>
              <p className="muted">
                Pick a change from the list to view its details, approvers, and timeline.
              </p>
            </div>
          </section>
        </div>
      )}

      {showCreate ? (
        <CreateChangeModal
          cfg={cfg}
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            setShowCreate(false);
            reload();
            navigate(`/change-management/${id}`);
          }}
        />
      ) : null}
    </>
  );
}

// ===========================================================================
// Board (Kanban) view — full-width, grouped by configured status, drag a card
// to another column to PATCH its status. Reuses the shared `.board-*` styles,
// mirroring the work-order (Facilities) board.
// ===========================================================================

function ChangeBoard({
  statuses,
  requests,
  loading,
  error,
  reload,
  onOpen,
}: {
  statuses: ChangeOption[];
  requests: ChangeRequestRow[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  onOpen: (id: string) => void;
}) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [movingId, setMovingId] = useState<string | null>(null);
  const moveMut = useMutation((vars: { id: string; status: string }) =>
    updateChangeRequest(vars.id, { status: vars.status }),
  );

  const handleDrop = useCallback(
    async (id: string, statusCode: string, currentStatus: string) => {
      setDragId(null);
      if (statusCode === currentStatus) return;
      setMovingId(id);
      await moveMut.run({ id, status: statusCode });
      setMovingId(null);
      reload();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [moveMut.run, reload],
  );

  if (loading) return <Loading label="Loading board…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const columns = statuses;
  const known = new Set(columns.map((c) => c.code));
  const byStatus = (code: string) => requests.filter((r) => r.status === code);
  const orphanCount = requests.filter((r) => !known.has(r.status)).length;

  return (
    <div className="board-content">
      {moveMut.error ? <Alert kind="error">Couldn't move the change: {moveMut.error}</Alert> : null}
      <div className="board">
        {columns.map((col, idx) => (
          <ChangeColumn
            key={col.code}
            column={col}
            // Surface orphan-status changes in the leftmost column so they're reachable.
            requests={idx === 0 ? [...byStatus(col.code), ...requests.filter((r) => !known.has(r.status))] : byStatus(col.code)}
            dragId={dragId}
            movingId={movingId}
            onDragState={setDragId}
            onDrop={handleDrop}
            onOpen={onOpen}
            note={idx === 0 && orphanCount > 0 ? `Includes ${orphanCount} with other statuses` : null}
          />
        ))}
      </div>
    </div>
  );
}

function ChangeColumn({
  column,
  requests,
  dragId,
  movingId,
  onDragState,
  onDrop,
  onOpen,
  note,
}: {
  column: ChangeOption;
  requests: ChangeRequestRow[];
  dragId: string | null;
  movingId: string | null;
  onDragState: (id: string | null) => void;
  onDrop: (id: string, statusCode: string, currentStatus: string) => void;
  onOpen: (id: string) => void;
  note: string | null;
}) {
  const [dropOver, setDropOver] = useState(false);
  const depth = useRef(0);

  function handleDragEnter(e: DragEvent<HTMLElement>) {
    e.preventDefault();
    depth.current += 1;
    setDropOver(true);
  }
  function handleDragLeave() {
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setDropOver(false);
  }
  function handleDragOver(e: DragEvent<HTMLElement>) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }
  function handleDrop(e: DragEvent<HTMLElement>) {
    e.preventDefault();
    depth.current = 0;
    setDropOver(false);
    const payload = e.dataTransfer.getData("text/plain");
    if (!payload) return;
    const [id, currentStatus] = payload.split("|");
    if (id) onDrop(id, column.code, currentStatus ?? "");
  }

  // Stable accent per status code so each column reads distinctly.
  const accents = ["var(--st-open)", "var(--st-progress)", "var(--st-done)", "var(--accent)", "var(--brand-2)"];
  let h = 0;
  for (let i = 0; i < column.code.length; i++) h = (h * 31 + column.code.charCodeAt(i)) >>> 0;
  const colAccent = column.color || accents[h % accents.length];

  return (
    <section
      className={`board-col${dropOver ? " col-dropover" : ""}`}
      aria-label={column.label}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <header className="board-col-head">
        <span className="col-swatch" style={{ ["--col-accent" as string]: colAccent }} aria-hidden="true" />
        <span className="col-name">{column.label}</span>
        <span className="grow" />
        <span className="count">{requests.length}</span>
      </header>
      {note ? <div className="board-col-wip">{note}</div> : null}
      <div className="board-col-body">
        {requests.length === 0 ? (
          <p className="muted board-empty">No changes</p>
        ) : (
          requests.map((r) => (
            <ChangeCard
              key={r.id}
              request={r}
              dragging={dragId === r.id}
              moving={movingId === r.id}
              onDragState={onDragState}
              onOpen={() => onOpen(r.id)}
            />
          ))
        )}
      </div>
    </section>
  );
}

function ChangeCard({
  request,
  dragging,
  moving,
  onDragState,
  onOpen,
}: {
  request: ChangeRequestRow;
  dragging: boolean;
  moving: boolean;
  onDragState: (id: string | null) => void;
  onOpen: () => void;
}) {
  const numFmt = useChangeNumberFormat();
  const rail =
    {
      high: "var(--st-blocked)",
      medium: "var(--st-progress)",
      low: "var(--st-closed)",
    }[(request.risk ?? "").toLowerCase()] ?? "var(--st-closed)";

  return (
    <article
      className={`board-card${moving ? " busy" : ""}${dragging ? " card-dragging" : ""}`}
      style={{ ["--card-rail" as string]: rail }}
      draggable
      onDragStart={(e) => {
        if ((e.target as HTMLElement).closest("select, button, input, a, option")) {
          e.preventDefault();
          return;
        }
        e.dataTransfer.setData("text/plain", `${request.id}|${request.status}`);
        e.dataTransfer.effectAllowed = "move";
        onDragState(request.id);
      }}
      onDragEnd={() => onDragState(null)}
    >
      <span className="board-card-origin mono faint">#{formatChangeNumber(request.number, numFmt)}</span>
      <button className="board-card-title" onClick={onOpen} title="Open change">
        {request.title}
      </button>
      <div className="board-card-foot">
        <ChangeRiskBadge risk={request.risk} />
        <ChangeTypeBadge type={request.change_type} />
        <span className="grow" />
      </div>
    </article>
  );
}

/** Urgency/Priority is a fixed three-level set (not tenant-configurable). */
export const CHANGE_URGENCY_OPTIONS: { code: string; label: string }[] = [
  { code: "high", label: "High" },
  { code: "moderate", label: "Moderate" },
  { code: "low", label: "Low" },
];
export const CHANGE_APPROVAL_OPTIONS: { code: string; label: string }[] = [
  { code: "yes", label: "Yes" },
  { code: "notification_only", label: "No Approval (Notification Only)" },
];
export const CHANGE_BUDGETED_OPTIONS: { code: string; label: string }[] = [
  { code: "yes", label: "Yes" },
  { code: "no", label: "No" },
  { code: "na", label: "N/A" },
];

/** A chip-list + add-dropdown multi-select over `{value,label}` options. The
 *  shared affected-campuses/buildings + contributors picker (mirrors the IT
 *  systems picker pattern). */
export function MultiSelectChips({
  label,
  options,
  selected,
  onChange,
  placeholder = "Add…",
  hint,
}: {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  hint?: string | undefined;
}) {
  const addable = options.filter((o) => !selected.includes(o.value));
  const labelOf = (v: string) => options.find((o) => o.value === v)?.label ?? v;
  return (
    <div className="field">
      <span className="label">{label}</span>
      {selected.length > 0 ? (
        <ul className="person-list">
          {selected.map((v) => (
            <li key={v} className="person-row">
              <span className="person-main">
                <span className="person-name">{labelOf(v)}</span>
              </span>
              <button
                type="button"
                className="person-remove"
                aria-label={`Remove ${labelOf(v)}`}
                onClick={() => onChange(selected.filter((x) => x !== v))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <select
        value=""
        onChange={(e) => {
          if (e.target.value) onChange([...selected, e.target.value]);
        }}
        aria-label={label}
        disabled={addable.length === 0}
      >
        <option value="">{addable.length === 0 ? "No more options" : placeholder}</option>
        {addable.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

function CreateChangeModal({
  cfg,
  onClose,
  onCreated,
}: {
  cfg: ChangeConfigIndex;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [changeType, setChangeType] = useState("");
  const [risk, setRisk] = useState(cfg.risks[0]?.code ?? "low");
  const [requestedBy, setRequestedBy] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [systemIds, setSystemIds] = useState<string[]>([]);
  // Extended intake fields (0103).
  const [urgency, setUrgency] = useState("");
  const [businessImpact, setBusinessImpact] = useState("");
  const [requiresApproval, setRequiresApproval] = useState("yes");
  const [targetDate, setTargetDate] = useState("");
  const [notifyEndusers, setNotifyEndusers] = useState(false);
  const [notificationDates, setNotificationDates] = useState("");
  const [automatedRecurring, setAutomatedRecurring] = useState(false);
  const [schedule, setSchedule] = useState("");
  const [campusesAffected, setCampusesAffected] = useState<string[]>([]);
  const [buildingsAffected, setBuildingsAffected] = useState<string[]>([]);
  const [contributors, setContributors] = useState<string[]>([]);
  const [summaryOfChange, setSummaryOfChange] = useState("");
  const [testingPlan, setTestingPlan] = useState("");
  const [isBudgeted, setIsBudgeted] = useState("");
  const [files, setFiles] = useState<File[]>([]);

  const assigneesApi = useApi((s) => listAssignees(s, { workType: "change_management", field: "assignee" }), []);
  const assignees = assigneesApi.data?.assignees ?? [];
  const contributorsApi = useApi((s) => listAssignees(s, { workType: "change_management", field: "contributor" }), []);
  const contributorOptions = (contributorsApi.data?.assignees ?? []).map((u) => ({ value: u.id, label: u.name }));
  const placesApi = useApi((s) => getPlaces(s), []);
  const campusOptions = (placesApi.data?.campuses ?? []).map((o) => ({ value: o.code, label: o.label }));
  // Buildings are filtered to the selected campuses (all when none chosen).
  const buildingOptions = (placesApi.data?.buildings ?? [])
    .filter((b) => campusesAffected.length === 0 || campusesAffected.includes(b.campus_code))
    .map((o) => ({ value: o.code, label: o.label }));
  const departmentsApi = useApi((s) => listDepartments(s), []);
  const cmDepartments = (departmentsApi.data?.departments ?? []).filter((d) => d.change_management);
  // Approvers: auto-populated from the selected department, but the field stays
  // editable so the filer can add or remove people manually.
  const deptApproversApi = useApi(
    (s) => (departmentId ? listDepartmentApprovers(departmentId, s) : Promise.resolve({ approvers: [] })),
    [departmentId],
  );
  const deptApprovers = deptApproversApi.data?.approvers ?? [];
  const [approverIds, setApproverIds] = useState<string[]>([]);
  // Replace the approver selection with the department's set whenever the
  // department's approver list (re)loads — which only happens on a department
  // change, so manual edits in between are preserved.
  useEffect(() => {
    if (!deptApproversApi.data) return;
    setApproverIds(deptApproversApi.data.approvers.map((a) => a.user_id));
  }, [deptApproversApi.data]);
  // Option pool for the manual picker: change assignees ∪ the department's
  // approvers (so every current selection resolves to a name and is removable).
  const approverOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of assignees) map.set(u.id, u.name);
    for (const a of deptApprovers) if (a.user_id) map.set(a.user_id, a.name ?? a.user_id);
    return [...map].map(([value, label]) => ({ value, label }));
  }, [assignees, deptApprovers]);
  const systemsApi = useApi((s) => listItSystems(undefined, s), []);
  const systems = systemsApi.data?.it_systems ?? [];
  const addableSystems = useMemo(() => systems.filter((s) => !systemIds.includes(s.id)), [systems, systemIds]);

  const createMut = useMutation((body: CreateChangeRequestInput) => createChangeRequest(body));

  // An approval-required change with no approvers is a dead-end (nobody can ever
  // approve it). Block submit + warn rather than create it.
  const approversMissing = requiresApproval === "yes" && approverIds.length === 0;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim() || approversMissing) return;
    const body: CreateChangeRequestInput = {
      title: title.trim(),
      ...(!richTextIsEmpty(description) ? { description } : {}),
      ...(changeType ? { change_type: changeType } : {}),
      risk,
      ...(requestedBy ? { requested_by: requestedBy } : {}),
      ...(start ? { scheduled_start: dateInputToUnix(start) } : {}),
      ...(end ? { scheduled_end: dateInputToUnix(end) } : {}),
      ...(departmentId ? { department_id: departmentId } : {}),
      ...(systemIds.length ? { system_ids: systemIds } : {}),
      ...(approverIds.length ? { approver_ids: approverIds } : {}),
      ...(urgency ? { urgency } : {}),
      ...(businessImpact ? { business_impact: businessImpact } : {}),
      ...(requiresApproval ? { requires_approval: requiresApproval } : {}),
      ...(targetDate ? { target_date: dateInputToUnix(targetDate) } : {}),
      notify_endusers: notifyEndusers,
      ...(notifyEndusers && notificationDates.trim() ? { notification_dates: notificationDates.trim() } : {}),
      automated_recurring: automatedRecurring,
      ...(automatedRecurring && schedule ? { schedule } : {}),
      ...(campusesAffected.length ? { campuses_affected: campusesAffected } : {}),
      ...(buildingsAffected.length ? { buildings_affected: buildingsAffected } : {}),
      ...(contributors.length ? { contributors } : {}),
      ...(!richTextIsEmpty(summaryOfChange) ? { summary_of_change: summaryOfChange } : {}),
      ...(!richTextIsEmpty(testingPlan) ? { testing_plan: testingPlan } : {}),
      ...(isBudgeted ? { is_budgeted: isBudgeted } : {}),
    };
    const res = await createMut.run(body);
    if (res) {
      // Upload any selected attachments now that the request id exists.
      for (const f of files) {
        try {
          await uploadChangeAttachment(res.request.id, f);
        } catch {
          /* surfaced on the detail page; don't block navigation */
        }
      }
      onCreated(res.request.id);
    }
  }

  return (
    <Modal
      title="New Change Request"
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="submit" form="create-change-form" disabled={createMut.pending || !title.trim() || approversMissing}>
            {createMut.pending ? "Creating…" : "Create change"}
          </button>
        </>
      }
    >
      <form id="create-change-form" onSubmit={submit} className="form-grid">
        {createMut.error ? <Alert kind="error">{createMut.error}</Alert> : null}
        <label className="field">
          <span className="label">Title</span>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />
        </label>
        <div className="field">
          <span className="label">Description</span>
          <RichTextEditor value={description} onChange={setDescription} placeholder="Describe the change…" />
        </div>
        <div className="row" style={{ gap: 12 }}>
          <label className="field" style={{ flex: 1 }}>
            <span className="label">Type</span>
            <select value={changeType} onChange={(e) => setChangeType(e.target.value)}>
              <option value="">—</option>
              {cfg.types.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field" style={{ flex: 1 }}>
            <span className="label">Risk</span>
            <select value={risk} onChange={(e) => setRisk(e.target.value)}>
              {cfg.risks.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="field">
          <span className="label">Requested by</span>
          <select value={requestedBy} onChange={(e) => setRequestedBy(e.target.value)}>
            <option value="">Me (default)</option>
            {assignees.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </label>
        <div className="row" style={{ gap: 12 }}>
          <label className="field" style={{ flex: 1 }}>
            <span className="label">Requires Approval</span>
            <select value={requiresApproval} onChange={(e) => setRequiresApproval(e.target.value)}>
              {CHANGE_APPROVAL_OPTIONS.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.label}
                </option>
              ))}
            </select>
            <span className="hint">
              {requiresApproval === "notification_only"
                ? "Department approvers are notified for awareness — no approval needed."
                : "Department approvers must approve before the change proceeds."}
            </span>
          </label>
          <label className="field" style={{ flex: 1 }}>
            <span className="label">Target Date</span>
            <input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
          </label>
        </div>

        <label className="field">
          <span className="label">Department</span>
          <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
            <option value="">— None —</option>
            {cmDepartments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <span className="hint">Filing under a department auto-populates its approvers.</span>
        </label>
        <MultiSelectChips
          label="Approvers"
          options={approverOptions}
          selected={approverIds}
          onChange={setApproverIds}
          placeholder={deptApproversApi.loading ? "Loading approvers…" : "Add an approver…"}
          hint="Auto-populated from the selected department — add or remove people as needed."
        />
        {approversMissing ? (
          <Alert kind="info">
            “Requires Approval” is set to Yes but no approvers are listed — add at least one, or set it to “No
            Approval (Notification Only)”.
          </Alert>
        ) : null}
        <label className="field">
          <span className="label">Scheduled start</span>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
        </label>
        <label className="field">
          <span className="label">Scheduled end</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
        </label>
        <div className="field">
          <span className="label">IT systems</span>
          {systemIds.length > 0 ? (
            <ul className="person-list">
              {systemIds.map((id) => {
                const s = systems.find((x) => x.id === id);
                return (
                  <li key={id} className="person-row">
                    <span className="person-main">
                      <span className="person-name">{s?.name ?? id}</span>
                    </span>
                    <button
                      type="button"
                      className="person-remove"
                      aria-label="Remove IT system"
                      onClick={() => setSystemIds(systemIds.filter((x) => x !== id))}
                    >
                      ×
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
          <select
            value=""
            onChange={(e) => {
              if (e.target.value) setSystemIds([...systemIds, e.target.value]);
            }}
            aria-label="Add IT system"
            disabled={addableSystems.length === 0}
          >
            <option value="">Add an IT system…</option>
            {addableSystems.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        <div className="row" style={{ gap: 12 }}>
          <label className="field" style={{ flex: 1 }}>
            <span className="label">Urgency / Priority</span>
            <select value={urgency} onChange={(e) => setUrgency(e.target.value)}>
              <option value="">—</option>
              {cfg.urgencies.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field" style={{ flex: 1 }}>
            <span className="label">Business Impact</span>
            <select value={businessImpact} onChange={(e) => setBusinessImpact(e.target.value)}>
              <option value="">—</option>
              {cfg.impacts.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="toggle-row" style={{ marginTop: 16, marginBottom: 16 }}>
          <label className="toggle">
            <input type="checkbox" checked={notifyEndusers} onChange={(e) => setNotifyEndusers(e.target.checked)} />
            <span>Requires notification to end users</span>
          </label>
          <label className="toggle">
            <input type="checkbox" checked={automatedRecurring} onChange={(e) => setAutomatedRecurring(e.target.checked)} />
            <span>Automated / Recurring</span>
          </label>
        </div>
        {notifyEndusers ? (
          <label className="field">
            <span className="label">Notification Dates</span>
            <input
              type="text"
              value={notificationDates}
              onChange={(e) => setNotificationDates(e.target.value)}
              placeholder="Single and/or recurring dates"
            />
          </label>
        ) : null}
        {automatedRecurring ? (
          <label className="field">
            <span className="label">Schedule</span>
            <select value={schedule} onChange={(e) => setSchedule(e.target.value)}>
              <option value="">—</option>
              {cfg.schedules.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <MultiSelectChips
          label="Campuses Affected"
          options={campusOptions}
          selected={campusesAffected}
          onChange={(next) => {
            setCampusesAffected(next);
            // Drop any selected buildings that no longer belong to a chosen campus.
            const allowed = new Set(
              (placesApi.data?.buildings ?? [])
                .filter((b) => next.length === 0 || next.includes(b.campus_code))
                .map((b) => b.code),
            );
            setBuildingsAffected((cur) => cur.filter((c) => allowed.has(c)));
          }}
          placeholder="Add a campus…"
          hint={campusOptions.length === 0 ? "No campuses configured in Settings → Facilities → Locations." : undefined}
        />
        <MultiSelectChips
          label="Buildings Affected"
          options={buildingOptions}
          selected={buildingsAffected}
          onChange={setBuildingsAffected}
          placeholder="Add a building…"
        />
        <MultiSelectChips
          label="Contributors"
          options={contributorOptions}
          selected={contributors}
          onChange={setContributors}
          placeholder="Add a contributor…"
          hint="People who will work on the change."
        />

        <div className="field">
          <span className="label">Summary of Change</span>
          <RichTextEditor value={summaryOfChange} onChange={setSummaryOfChange} placeholder="Summarize the change…" />
        </div>
        <div className="field">
          <span className="label">Testing Plan</span>
          <RichTextEditor value={testingPlan} onChange={setTestingPlan} placeholder="How will this change be tested…" />
        </div>
        <label className="toggle" style={{ marginTop: 16, marginBottom: 16 }}>
          <input
            type="checkbox"
            checked={isBudgeted === "yes"}
            onChange={(e) => setIsBudgeted(e.target.checked ? "yes" : "no")}
          />
          <span>Is this change budgeted?</span>
        </label>

        <div className="field">
          <span className="label">Attachments</span>
          {files.length > 0 ? (
            <ul className="att-list">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} className="att-item">
                  <span className="att-main" style={{ cursor: "default" }}>
                    <span className="att-info">
                      <span className="att-name">{f.name}</span>
                      <span className="att-sub">{formatBytes(f.size)}</span>
                    </span>
                  </span>
                  <button
                    type="button"
                    className="att-del"
                    aria-label={`Remove ${f.name}`}
                    onClick={() => setFiles(files.filter((_, j) => j !== i))}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <label className="btn small" style={{ cursor: "pointer", alignSelf: "flex-start" }}>
            + Add files
            <input
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                setFiles([...files, ...Array.from(e.target.files ?? [])]);
                e.target.value = "";
              }}
            />
          </label>
          {files.length > 0 ? (
            <span className="hint">Uploaded after the change is created.</span>
          ) : null}
        </div>
      </form>
    </Modal>
  );
}
